import isEqual from "lodash/isEqual";
import * as React from "react";

import type { Filter } from "./DataTableFilters";

export type DataTableSortDirection = "asc" | "desc";

/**
 * How distinct filters are joined when building the wire condition. `"AND"`
 * (the default) requires every filter to match; `"OR"` requires at least
 * one.
 */
export type DataTableMatchMode = "AND" | "OR";

/** Imperative actions exposed by a mounted `DataTable.Table`. Obtain via
 * `useDataTableState().tableRef` or call the convenience wrappers on
 * `DataTableState` (e.g. `clearSelection()`).
 */
export interface DataTableHandle {
  /**
   * Clear all selections
   */
  deselectAll(): void;
  /**
   * Show or hide a single column by colId. Caller passes the desired
   * next state; no read-back required. Internal columns (selection,
   * expand) are filtered by the picker UI, not enforced here.
   */
  setColumnVisibility(id: string, visible: boolean): void;
}

export interface DataTableSort {
  columnId: string;
  direction: DataTableSortDirection;
}

export type DataTableLoadingState = "foreground" | "background" | "idle";

/**
 * Canonical page-size choices surfaced by the DataTable footer. Single source
 * of truth across all DataTable consumers and any saved-preferences UI that
 * surfaces a "rows per page" control. Don't fork this list per-domain.
 */
export const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

/**
 * Saved user preferences seeded into the table at mount. The wire shape
 * (`TableUIPreferences`) uses snake_case; this is the camelCase view consumers
 * receive from the per-domain preferences hooks (e.g. `useDatasetTablePreferences`).
 */
export interface DataTablePreferences {
  columns?: string[];
  rowsPerPage?: number;
}

interface UseDataTableStateOptions<T> {
  /**
   * Pure row → id projection. Required so the table knows how
   * to retrieve the unique key from the data.
   */
  getRowId: (row: T) => string;

  /**
   * Initial sort applied on first render. Captured once at mount; later
   * changes to this prop are ignored — the hook is the source of truth from
   * then on.
   */
  initialSort?: DataTableSort;

  /**
   * Saved user preferences (page size, column order). Captured once at
   * mount; consumers should fetch preferences before rendering the table
   * (e.g. via a parent suspense boundary) so they're available on first render.
   * Later changes to this prop are ignored.
   */
  preferences?: DataTablePreferences;

  /**
   * Fired when the user changes a persisted preference. Receives the full
   * merged preferences object — the hook tracks the latest emitted shape so
   * editing one dimension (e.g. page size) doesn't clobber a prior edit to
   * another (e.g. column order). The consumer is responsible for persisting.
   */
  onPreferencesChange?: (next: DataTablePreferences) => void;

  /**
   * Initial applied filters seeded into the hook at mount. Captured once;
   * later changes to this prop are ignored — the hook owns filter state from
   * then on, and consumers append to it via `onFiltersChange`. Use this for
   * restoring saved or URL-encoded filter sets at initial render.
   */
  initialFilters?: Filter[];

  /**
   * Initial match mode seeded into the hook at mount. Captured once;
   * defaults to `"AND"`.
   */
  initialMatchMode?: DataTableMatchMode;

  /**
   * Default ordered list of visible column ids, used when no
   * `preferences.columns` are present at mount. Pass `undefined` to
   * defer entirely to ColDef-level `hide` defaults — the hook treats
   * an absent option differently from an explicit empty array (which
   * means "hide everything by default").
   */
  defaultVisibleColumnIds?: string[];
}

/**
 * Bundle consumed by `<DataTable.ActiveFilters>`. Spread it onto the
 * component: `<DataTable.ActiveFilters {...tableState.activeFilters}
 * filterDefinitions={defs} />`.
 */
export interface DataTableActiveFiltersSlice {
  filters: Filter[];
  onRemove: (index: number) => void;
  onClear: () => void;
  matchMode: DataTableMatchMode;
  onMatchModeChange: (next: DataTableMatchMode) => void;
  onFilterSelected: (filter: Filter) => void;
}

/**
 * Bundle consumed by `<DataTable.Table>`. Spread it onto the component:
 * `<DataTable.Table {...tableState.table} ... />`.
 *
 * Parameterized over the row type `T` so the selection callback can hand
 * the hook full `T` rows (not just ids) — this is what powers cross-page
 * selection accumulation. The `T` here must match the `TData` passed to
 * `<DataTable.Table<TData>>` or selection state will be misshapen.
 */
export interface DataTableSlice<T> {
  /**
   * Initial sort applied to the table on first render. Read once at mount;
   * later changes are ignored — the table component is the source of truth
   * from then on, and `onSortChange` flows user-initiated sort changes back
   * to the hook.
   */
  initialSort: DataTableSort | null;
  onSortChange: (sort: DataTableSort | null) => void;
  /**
   * Fired when the user toggles a single column's visibility (via the
   * picker or any other affordance that ends up calling
   * `gridApi.setColumnsVisible`). The hook reduces this into the
   * canonical ordered visible-column list and emits prefs.
   */
  onVisibleColumnChange: (id: string, visible: boolean) => void;
  /**
   * Fired when the user re-orders columns by drag. The hook treats the
   * argument as the new full ordered list of visible column ids and
   * persists it wholesale.
   */
  onColumnOrderChange: (orderedIds: string[]) => void;
  /**
   * Pure row → id projection. Captured once at mount in the hook (single
   * source of truth) and forwarded to AG Grid via `<DataTable.Table>`.
   * The same function powers the cross-page selection accumulator and
   * AG Grid's per-row identity, so both layers stay aligned.
   */
  getRowId: (row: T) => string;
  /**
   * Fired by `DataTableTable` for a single row toggling its selection.
   */
  onRowSelectionChange: (row: T, id: string, isSelected: boolean) => void;
  /**
   * Fired by `DataTableTable` for "select all" / "deselect all"
   */
  onBulkSelectionChange: (selectedRows: T[], affectedIds: Set<string>) => void;
  /**
   * Cross-page selection accumulator keyed by row id.
   */
  selectionMap: ReadonlyMap<string, T>;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  currentPage: number;
  nextPage: () => void;
  prevPage: () => void;
  canGetPrevPage: boolean;
  /** Populated by `DataTableTable` via `useImperativeHandle`. */
  tableRef?: React.RefObject<DataTableHandle>;
}

/**
 * Bundle consumed by `<DataTable.Selection>`. Spread it onto the component:
 * `<DataTable.Selection {...tableState.selectionSlice} options={...} />`.
 */
export interface DataTableSelectionSlice<T> {
  selection: T[];
  clearSelection: () => void;
}

/**
 * Returned from `useDataTableState`. The top-level fields (`sort`, `pageSize`,
 * `selection`, `currentPage`) are for the consumer to read — typically to
 * build queries, drive bulk-action UIs, or pass `currentPage` into a paginated
 * data hook. The `table` slice is a pre-shaped bundle meant to be spread onto
 * `<DataTable.Table>`; consumers shouldn't pluck individual fields out of it.
 *
 * `sort`, `pageSize`, and `currentPage` deliberately appear at top-level *and*
 * on the slice — same value, two audiences (consumer query-building vs the
 * table component).
 */
export interface DataTableState<T> {
  sort: DataTableSort | null;
  pageSize: number;
  /**
   * Currently-selected rows, accumulated across pages.
   * Empty unless `table` is spread onto `<DataTable.Table>`.
   */
  selection: T[];

  /**
   * Current page (zero-indexed)
   */
  currentPage: number;

  /**
   * Set of column ids currently visible in the table.
   */
  visibleColumnIds: Set<string>;

  /**
   * Show or hide a single column by colId. The hook delegates to the
   * mounted table's imperative handle, which calls
   * `gridApi.setColumnsVisible`. AG Grid is the source of truth — the
   * resulting `onColumnVisible` event flows back through the slice
   * callback to update React state and persist prefs.
   */
  setColumnVisibility: (id: string, visible: boolean) => void;

  /**
   * Currently-applied filters. Read this to build the QuerySpec condition
   * (typically via `filterToCondition`).
   */
  filters: Filter[];

  /**
   * How distinct filters are joined when building the wire condition.
   * Pass this into `filterToCondition(filters, { matchMode })` so the
   * outer wrap is `AND` or `OR` accordingly.
   */
  matchMode: DataTableMatchMode;

  /**
   * Set the match mode. Resets `currentPage` to 0 because the underlying
   * query changes.
   */
  setMatchMode: (next: DataTableMatchMode) => void;

  /**
   * Appends a single filter to the applied list. Use this in
   * `<DataTable.Filters onFilterSelected>` — no need to read `filters` back
   * from state. Always resets `currentPage` to 0.
   */
  addFilter: (filter: Filter) => void;

  /**
   * Removes the filter at the given index from the applied list. Always
   * resets `currentPage` to 0 because the underlying query changes.
   */
  removeFilter: (index: number) => void;

  /**
   * Clears every applied filter and resets `matchMode` to the value
   * captured from `initialMatchMode` at mount (default `"AND"`).
   * Always resets `currentPage` to 0.
   */
  clearFilters: () => void;

  /**
   * Reset `currentPage` to 0. Already called internally on `sort`,
   * `pageSize`, and `filters` changes (any input owned by this hook that
   * affects the underlying query).
   */
  resetCurrentPage: () => void;

  /**
   * Clears all selected rows — empties the cross-page accumulator and
   * calls `gridApi.deselectAll()` on the mounted table.
   */
  clearSelection: () => void;

  table: DataTableSlice<T>;
  activeFilters: DataTableActiveFiltersSlice;
  selectionSlice: DataTableSelectionSlice<T>;
}

export function useDataTableState<T>(
  options: UseDataTableStateOptions<T>,
): DataTableState<T> {
  const {
    getRowId,
    initialSort: initialSortOption,
    preferences,
    onPreferencesChange,
    initialFilters: initialFiltersOption,
    initialMatchMode: initialMatchModeOption,
    defaultVisibleColumnIds,
  } = options;

  // Capture `getRowId` once at mount so the cross-page selection
  // accumulator sees a single identity strategy, matching the same lock-in
  // applied by `DataTableTable` on its own `getRowId` prop. A different
  // identity strategy would split selection entries between key spaces;
  // remount the hook (e.g. via a parent `key` prop) to switch.
  const stableGetRowIdRef = React.useRef(getRowId);

  // Snapshot initial preferences once at mount
  const [snapshotPreferences] = React.useState<DataTablePreferences>(
    () => preferences ?? {},
  );

  // Snapshot of the initial sort, exposed to the table via the slice. Captured
  // once at mount; later user sorts are tracked separately in `sort` and
  // emitted via `onSortChange`.
  const [initialSort] = React.useState<DataTableSort | null>(
    initialSortOption ?? null,
  );
  const [sort, setSort] = React.useState<DataTableSort | null>(initialSort);
  const [pageSize, setPageSize] = React.useState(
    snapshotPreferences.rowsPerPage ?? 50,
  );

  const emptySelectionMap = React.useMemo(() => new Map<string, T>(), []);
  const [selectionMap, setSelectionMap] =
    React.useState<Map<string, T>>(emptySelectionMap);
  const selection = React.useMemo<T[]>(
    () => Array.from(selectionMap.values()),
    [selectionMap],
  );

  const tableRef = React.useRef<DataTableHandle>(null);

  const clearSelection = React.useCallback(() => {
    setSelectionMap(emptySelectionMap);
    tableRef.current?.deselectAll();
  }, [emptySelectionMap]);

  const [currentPage, setCurrentPage] = React.useState(0);

  // Snapshot of initial filters; the live `filters` array drives consumer
  // query-building. Snapshot mirrors `initialSort` above so late prop
  // changes can't reseed the filter state.
  const [initialFilters] = React.useState<Filter[]>(
    () => initialFiltersOption ?? [],
  );
  const [filters, setFilters] = React.useState<Filter[]>(initialFilters);

  // Match-mode is owned by the hook from mount onward; later prop changes
  // are ignored, mirroring the `initialFilters` snapshot pattern.
  const [initialMatchMode] = React.useState<DataTableMatchMode>(
    () => initialMatchModeOption ?? "AND",
  );
  const [matchMode, setMatchModeState] =
    React.useState<DataTableMatchMode>(initialMatchMode);

  // `null` snapshot means "neither prefs nor defaults were provided" —
  // the slice exposes it as-is so the table can skip `initialState`
  // seeding and let ColDef-level `hide` defaults take effect. An empty
  // array is preserved as a real "explicitly nothing visible" intent.
  const [initialVisibleColumnIds] = React.useState<string[] | null>(
    () => snapshotPreferences.columns ?? defaultVisibleColumnIds ?? null,
  );
  const [visibleColumnList, setVisibleColumnList] = React.useState<string[]>(
    () => initialVisibleColumnIds ?? [],
  );

  // Convenience wrapper for consumers
  const visibleColumnIds = React.useMemo(
    () => new Set(visibleColumnList),
    [visibleColumnList],
  );

  // Track the latest emitted preferences so column-order edits and page-size
  // edits merge into each other rather than clobbering. Seeded from the mount
  // snapshot; updated whenever we fire `onPreferencesChange`.
  const latestPreferencesRef =
    React.useRef<DataTablePreferences>(snapshotPreferences);

  const emitPreferences = React.useCallback(
    (next: DataTablePreferences) => {
      latestPreferencesRef.current = next;
      onPreferencesChange?.(next);
    },
    [onPreferencesChange],
  );

  const resetCurrentPage = React.useCallback(() => {
    setCurrentPage(0);
    setSelectionMap(emptySelectionMap);
  }, [emptySelectionMap]);

  const onSortChange = React.useCallback(
    (next: DataTableSort | null) => {
      setSort(next);
      // Sort changes the underlying query, so reset to the first page.
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const onPageSizeChange = React.useCallback(
    (size: number) => {
      setPageSize(size);
      emitPreferences({
        ...latestPreferencesRef.current,
        rowsPerPage: size,
      });
      // Pagesize changes the underlying query, so reset to first page
      resetCurrentPage();
    },
    [emitPreferences, resetCurrentPage],
  );

  const nextPage = React.useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const prevPage = React.useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const onRowSelectionChange = React.useCallback(
    (row: T, id: string, isSelected: boolean) => {
      setSelectionMap((prev) => {
        const next = new Map(prev);
        if (isSelected) {
          next.set(id, row);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [],
  );

  const onBulkSelectionChange = React.useCallback(
    (selectedRows: T[], affectedIds: Set<string>) => {
      const projectRowId = stableGetRowIdRef.current;
      setSelectionMap((prev) => {
        const next = new Map(prev);
        const selectedIdsInScope = new Set<string>();
        for (const row of selectedRows) {
          const id = projectRowId(row);
          selectedIdsInScope.add(id);
          next.set(id, row);
        }
        // For ids the bulk op affected that aren't in scope are deselected
        for (const id of affectedIds) {
          if (!selectedIdsInScope.has(id)) {
            next.delete(id);
          }
        }
        return next;
      });
    },
    [],
  );

  // setColumnVisibility flows: hook → tableRef → gridApi → AG Grid
  // event → onVisibleColumnChange slice callback → setVisibleColumnList.
  // The grid is the source of truth; React state mirrors it. The
  // round-trip avoids optimistic state diverging from AG Grid's view.
  const setColumnVisibility = React.useCallback(
    (id: string, visible: boolean) => {
      tableRef.current?.setColumnVisibility(id, visible);
    },
    [],
  );

  const onVisibleColumnChange = React.useCallback(
    (id: string, visible: boolean) => {
      setVisibleColumnList((prev) => {
        if (prev.includes(id) === visible) {
          return prev;
        }
        const next = visible
          ? [...prev, id]
          : prev.filter((columnId) => columnId !== id);
        emitPreferences({
          ...latestPreferencesRef.current,
          columns: next,
        });
        return next;
      });
    },
    [emitPreferences],
  );

  const onColumnOrderChange = React.useCallback(
    (orderedIds: string[]) => {
      setVisibleColumnList((prev) => {
        if (isEqual(prev, orderedIds)) {
          return prev;
        }
        emitPreferences({
          ...latestPreferencesRef.current,
          columns: orderedIds,
        });
        return orderedIds;
      });
    },
    [emitPreferences],
  );

  const addFilter = React.useCallback(
    (filter: Filter) => {
      setFilters((prev) => [...prev, filter]);
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const removeFilter = React.useCallback(
    (index: number) => {
      setFilters((prev) => prev.filter((_, i) => i !== index));
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const clearFilters = React.useCallback(() => {
    setFilters([]);
    setMatchModeState(initialMatchMode);
    resetCurrentPage();
  }, [initialMatchMode, resetCurrentPage]);

  const setMatchMode = React.useCallback(
    (next: DataTableMatchMode) => {
      setMatchModeState(next);
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const canGetPrevPage = currentPage > 0;

  const table = React.useMemo<DataTableSlice<T>>(
    () => ({
      initialSort,
      onSortChange,
      onVisibleColumnChange,
      onColumnOrderChange,
      getRowId: stableGetRowIdRef.current,
      onRowSelectionChange,
      onBulkSelectionChange,
      selectionMap,
      pageSize,
      onPageSizeChange,
      currentPage,
      nextPage,
      prevPage,
      canGetPrevPage,
      tableRef,
    }),
    [
      initialSort,
      onSortChange,
      onVisibleColumnChange,
      onColumnOrderChange,
      onRowSelectionChange,
      onBulkSelectionChange,
      selectionMap,
      pageSize,
      onPageSizeChange,
      currentPage,
      nextPage,
      prevPage,
      canGetPrevPage,
    ],
  );

  const activeFilters = React.useMemo<DataTableActiveFiltersSlice>(
    () => ({
      filters,
      onRemove: removeFilter,
      onClear: clearFilters,
      matchMode,
      onMatchModeChange: setMatchMode,
      onFilterSelected: addFilter,
    }),
    [filters, removeFilter, clearFilters, matchMode, setMatchMode, addFilter],
  );

  const selectionSlice = React.useMemo<DataTableSelectionSlice<T>>(
    () => ({ selection, clearSelection }),
    [selection, clearSelection],
  );

  return React.useMemo<DataTableState<T>>(
    () => ({
      sort,
      pageSize,
      selection,
      currentPage,
      filters,
      matchMode,
      setMatchMode,
      addFilter,
      removeFilter,
      clearFilters,
      resetCurrentPage,
      clearSelection,
      visibleColumnIds,
      setColumnVisibility,
      table,
      activeFilters,
      selectionSlice,
    }),
    [
      sort,
      pageSize,
      selection,
      currentPage,
      filters,
      matchMode,
      setMatchMode,
      addFilter,
      removeFilter,
      clearFilters,
      activeFilters,
      resetCurrentPage,
      clearSelection,
      visibleColumnIds,
      setColumnVisibility,
      table,
      selectionSlice,
    ],
  );
}
